/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import {
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import moment from "moment";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import { LoadingButton } from "@mui/lab";
import { getProfitLossList } from "app/redux/slice/vendorSlice";

const validationSchema = yup.object().shape({
  toDate: yup.date().when("fromDate", (fromDate, schema) => {
    if (fromDate) {
      return schema
        .required("To Date is required")
        .min(fromDate, "To Date cannot be before From Date");
    }
    return schema;
  }),
});

const ProfitLoss = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);

  const getData = async (values, formikHelpers = {}) => {
    const { setSubmitting } = formikHelpers;

    if (setSubmitting) setSubmitting(true);
    const value = {
      fromDate: values?.fromDate,
      toDate: values?.toDate,
    };

    const response = await dispatch(getProfitLossList(value)).unwrap();
    if (response?.status === 200) {
      const tempObj = response?.data?.data?.result;
      const greater =
        tempObj?.income?.length > tempObj?.expense?.length
          ? tempObj?.income.length
          : tempObj?.expense.length;
      let tempArr = [];

      for (let i = 0; i <= greater - 1; i++) {
        let temp = {};
        temp.income_date = tempObj?.income[i]
          ? tempObj?.income[i].transactionDate
          : "";
        temp.income_des = tempObj?.income[i] ? tempObj?.income[i].note : "";
        temp.income = tempObj?.income[i]
          ? +tempObj?.income[i].creditAmount
          : "";

        temp.expense_date = tempObj?.expense[i]
          ? tempObj?.expense[i].transactionDate
          : "";
        temp.expense_des = tempObj?.expense[i] ? tempObj?.expense[i].note : "";
        temp.expense = tempObj?.expense[i]
          ? +tempObj?.expense[i].debitAmount
          : "";

        tempArr.push(temp);
      }
      setData(tempArr);
      if (setSubmitting) setSubmitting(false);
    } else {
      enqueueSnackbar(response.data?.response_message, { variant: "error" });
    }
  };

  const formik = useFormik({
    initialValues: {
      fromDate: "",
      toDate: "",
    },
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: getData,
  });
  const { values, handleSubmit, isSubmitting } = formik;

  useEffect(() => {
    getData({}, {});
  }, []);
  const totalDebit = data.reduce((sum, item) => sum + +item.expense ?? 0, 0);
  const totalCredit = data.reduce((sum, item) => sum + +item.income ?? 0, 0);

  return (
    <>
      <Card sx={{ display: "flex", mb: 3 }}>
        <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
          <CardContent>
            <FormikProvider value={formik}>
              <Form noValidate autoComplete="off">
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={5}>
                    <JumboTextField
                      type="date"
                      label={"From Date"}
                      name="fromDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      value={values?.fromDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <JumboTextField
                      type="date"
                      label={"To Date"}
                      name="toDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      value={values?.toDate}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <LoadingButton
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleSubmit();
                      }}
                      loading={isSubmitting}
                      loadingIndicator={<CircularProgress size={20} />}
                    >
                      Submit
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </Div>
      </Card>

      <Card>
        <CardContent>
          {data.length === 0 ? (
            <JumboListNoDataPlaceholder />
          ) : (
            <>
              <TableContainer component={Paper} sx={{ margin: "auto" }}>
                <Typography variant="h5" align="center" sx={{ mt: 2, mb: 2 }}>
                  {values?.rider?.first_name} {values?.rider?.last_name}
                  {" Profit & Loss Report "}
                  {values.fromDate && values.toDate && (
                    <>
                      {`: ${moment(values.fromDate, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )} - ${moment(values.toDate, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )}`}
                    </>
                  )}
                </Typography>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong> Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong> Description</strong>
                      </TableCell>
                      <TableCell sx={{ borderRight: "2px solid #cfc5c5" }}>
                        <strong>Expense</strong>
                      </TableCell>

                      <TableCell sx={{ borderLeft: "2px solid #cfc5c5" }}>
                        <strong> Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong> Description</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Income</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.expense_date
                            ? moment(item.expense_date).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>{item.expense_des}</TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #cfc5c5",
                            color: "red",
                          }}
                        >
                          {item.expense}
                        </TableCell>

                        <TableCell sx={{ borderLeft: "2px solid #cfc5c5" }}>
                          {item.income_date
                            ? moment(item.income_date).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>{item.income_des}</TableCell>
                        <TableCell sx={{ color: "green" }}>
                          {item.income}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                      >
                        {totalDebit}
                      </TableCell>

                      <TableCell
                        colSpan={2}
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                          borderLeft: "2px solid #cfc5c5",
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}
                      >
                        {totalCredit}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <div
                style={{
                  marginTop: "20px",
                  textAlign: "center",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  padding: "10px",
                  borderRadius: "8px",
                  backgroundColor:
                    totalCredit - totalDebit >= 0 ? "#e0f7e9" : "#fdecea",
                  color: totalCredit - totalDebit >= 0 ? "#2e7d32" : "#c62828",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                {totalCredit - totalDebit >= 0
                  ? `Net Profit : ₹ ${totalCredit - totalDebit}`
                  : `Net Loss : ₹ ${Math.abs(totalCredit - totalDebit)}`}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ProfitLoss;
